<script setup lang="ts">
import { computed } from 'vue';
import { useLayout } from '@/layout/composables/layout';
import { useLaunchChecklistStore } from '@/stores/launchChecklistStore';
import checklistBadge from '@/assets/images/launch-checklist/checklist-badge.svg';

/**
 * Entry point to the launch checklist, at the foot of the sidebar so it is in view on every page
 * while an organization still has setup to do. It disappears for good once the checklist is done.
 *
 * The sidebar rather than a page header: the desktop topbar is hidden (`lg:!hidden` in AppLayout)
 * and `.content-breadcrumb` is `display: none` above the layout breakpoint, so the nav is the only
 * chrome actually on screen everywhere — the same reason the trial chip lives here.
 */
const launchChecklist = useLaunchChecklistStore();
const { isSlim } = useLayout();

const title = computed(() => {
    const left = launchChecklist.remainingCount;
    return `${left} ${left === 1 ? 'item' : 'items'} left to set up before you go live. Open the launch checklist.`;
});
</script>

<template>
    <div v-if="launchChecklist.isActive" class="launch-chip-slot" :class="{ 'launch-chip-slot--slim': isSlim }">
        <button
            type="button"
            class="launch-chip"
            :title="title"
            :aria-label="title"
            @click="launchChecklist.openModal()"
        >
            <img :src="checklistBadge" alt="" class="launch-chip__icon" />
            <span v-if="!isSlim" class="launch-chip__label">Launch checklist</span>
            <span v-if="!isSlim" class="launch-chip__count">{{ launchChecklist.remainingCount }}</span>
        </button>
    </div>
</template>

<style scoped>
/* Matches the trial chip's gutters so the two stack cleanly at the foot of the sidebar. */
.launch-chip-slot {
    padding: 0 1rem 0.5rem;
}

.launch-chip-slot--slim {
    display: flex;
    justify-content: center;
    padding: 0 0 0.5rem;
}

.launch-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #b6c6cf;
    border-radius: 8px;
    background: #ffffff;
    color: #22344a;
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
}

.launch-chip:hover {
    background: #f7f9fa;
    border-color: #96aab6;
}

.launch-chip:focus-visible {
    outline: 2px solid #08334b;
    outline-offset: 2px;
}

.launch-chip__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

.launch-chip__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* How much is left, so the chip says something the tag in the nav does not. */
.launch-chip__count {
    margin-left: auto;
    padding: 1px 7px;
    border-radius: 10px;
    background: #fdf3df;
    color: #b07a1a;
    font-size: 0.6875rem;
    flex-shrink: 0;
}

/* Icon-only in the collapsed rail: the label has nowhere to go, and the title carries the detail. */
.launch-chip-slot--slim .launch-chip {
    width: auto;
    padding: 8px;
}
</style>
